import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  useGetServiceRequestsInfiniteQuery,
  useGetServiceRequestsSummaryQuery,
  usePrintServiceRequest,
  type ServiceRequestInfiniteQueryArgs
} from '@/entities/service-request';
import { useProjectSiteParams, usePermissions, useUrlState, useProjectSiteNavigation } from '@/shared/lib';
import { RouteNames } from '@/shared/config/routes';

import type { ServiceRequestTableData } from '../model/types';
import { getServiceRequestActions } from '../model';
import { DEFAULT_URL_STATE } from './constants';

export const useServiceRequests = () => {
  const { t } = useTranslation();
  const { projectId, siteId, isValid } = useProjectSiteParams();
  const { isReadOnly } = usePermissions();
  const { navigateWithContext } = useProjectSiteNavigation();
  const [urlState, setUrlState] = useUrlState(DEFAULT_URL_STATE);

  const [printingIds, setPrintingIds] = useState<string[]>([]);
  const [rejectTarget, setRejectTarget] = useState<ServiceRequestTableData | null>(null);

  const queryArgs: ServiceRequestInfiniteQueryArgs = useMemo(
    () => ({
      projectId: projectId ? Number(projectId) : undefined,
      siteId: siteId ? Number(siteId) : undefined,
      status: urlState.status,
      search: urlState.search,
      sortField: urlState.sortField,
      sortDirection: urlState.sortDirection,
      size: 20
    }),
    [projectId, siteId, urlState]
  );

  const { data, isLoading, isFetchingNextPage, hasNextPage, fetchNextPage, refetch, error } =
    useGetServiceRequestsInfiniteQuery(queryArgs, {
      skip: !isValid
    });

  const { data: summaryData, isLoading: isSummaryLoading } = useGetServiceRequestsSummaryQuery(
    {
      projectId,
      siteId,
      search: urlState.search
    },
    {
      skip: !isValid || !projectId || !siteId
    }
  );

  // Print action hook
  const { printServiceRequest } = usePrintServiceRequest({
    onSuccess: () => {
      toast.success(t('serviceRequests.printSuccess', 'Form downloaded successfully'));
    },
    onError: () => {
      toast.error(t('serviceRequests.printError', 'Failed to download form'));
    }
  });

  const handleMoveToConsignment = useCallback(
    (row: ServiceRequestTableData) => {
      navigateWithContext(RouteNames.CONSIGNMENT_NEW, {}, {}, { serviceRequestId: row.id });
    },
    [navigateWithContext]
  );

  const handleReject = useCallback((row: ServiceRequestTableData) => {
    setRejectTarget(row);
  }, []);

  const closeRejectModal = useCallback(() => {
    setRejectTarget(null);
  }, []);

  const handlePrintForm = useCallback(
    async (row: ServiceRequestTableData) => {
      setPrintingIds((ids) => [...ids, row.id]);
      try {
        await printServiceRequest({ id: Number(row.id) });
      } finally {
        setPrintingIds((ids) => ids.filter((id) => id !== row.id));
      }
    },
    [printServiceRequest]
  );

  const actions = useMemo(
    () =>
      getServiceRequestActions(
        t,
        {
          onMoveToConsignment: handleMoveToConsignment,
          onReject: handleReject,
          onPrintForm: handlePrintForm
        },
        isReadOnly,
        printingIds
      ),
    [t, handleMoveToConsignment, handleReject, handlePrintForm, isReadOnly, printingIds]
  );

  const tableData = useMemo(() => {
    if (!data?.pages) return [];
    return data.pages.flatMap((page) =>
      page.content.map((item) => ({
        ...item,
        id: String(item.id) // Convert number id to string for DataTable
      }))
    ) as ServiceRequestTableData[];
  }, [data]);

  const totalCount = data?.pages?.[0]?.totalElements ?? 0;

  const handleSort = useCallback(
    (field: string, direction: 'asc' | 'desc' | null) => {
      if (direction) {
        setUrlState({
          sortField: field,
          sortDirection: direction
        });
      }
    },
    [setUrlState]
  );

  return {
    // Data
    tableData,
    totalCount,
    summaryData,

    // Loading states
    isLoading,
    isFetchingNextPage,
    isSummaryLoading,
    hasNextPage,

    // Sorting
    sortField: urlState.sortField,
    sortDirection: urlState.sortDirection as 'asc' | 'desc',
    handleSort,

    // Actions
    actions,

    // Reject modal
    rejectTarget,
    closeRejectModal,

    // Error
    error,

    // API Actions
    fetchNextPage,
    refetch
  };
};
